import LegalLayout, { LegalHero, LegalSection, LegalList } from "@/components/LegalLayout";

export default function TermsPage() {
  return (
    <>
      <LegalHero
        eyebrow="LEGAL · TERMS OF SERVICE"
        title="Terms of Service"
        subtitle="The ground rules for using RADAR. We've kept the language plain — read once and get on with the work."
        updated="2026"
      />
      <LegalLayout>
        <LegalSection id="usage" title="1. Platform Usage">
          <p>
            RADAR (the "Platform") is an AI-assisted business intelligence and account research service
            offered by RADAR — Real-Time Account Intelligence &amp; Personalized Outreach Engine. The
            Platform is intended for professional and business use by Business Development teams,
            account managers, pre-sales architects, and GTM leaders.
          </p>
          <p>
            By creating an account or using the Platform you ("the User") agree to be bound by these Terms.
            If you do not agree, do not use the Platform.
          </p>
        </LegalSection>

        <LegalSection id="responsibilities" title="2. User Responsibilities">
          <p>
            RADAR generates AI-assisted insights, account intelligence reports, deal recommendations,
            and outreach drafts. These outputs are <strong className="text-white">decision-support
            artefacts, not decisions themselves</strong>. Users are responsible for independently
            validating AI-generated insights before making strategic, commercial, legal, or financial decisions.
          </p>
          <p>
            You agree to (a) provide accurate account and login information, (b) keep your credentials secure,
            and (c) not share your account with third parties outside your organisation.
          </p>
        </LegalSection>

        <LegalSection id="subscription" title="3. Subscription Terms">
          <LegalList items={[
            "Free Plan: limited to 3 reports/month, 1 enhancement use, 1 PDF download per month — at no charge.",
            "Pro Monthly: billed monthly at INR 250; includes 5 reports, 5 downloads, unlimited enhancements within the plan's report quota.",
            "Pro Yearly: billed annually at INR 2,750; includes 50 reports + 50 downloads per year.",
            "CRM Elite: billed annually at INR 50,000 per login per year; includes 100 reports + 100 downloads + full CRM pipeline tracking.",
            "Paid subscriptions auto-renew at the end of each billing cycle unless cancelled prior.",
            "Cancellation takes effect at the end of the current billing cycle; users retain access until then.",
            "Once a subscription expires, accounts enter read-only mode for 1 year, during which historical reports and CRM data remain visible but no new reports / enhancements / downloads can be generated.",
          ]} />
        </LegalSection>

        <LegalSection id="ip" title="4. Intellectual Property">
          <p>
            The RADAR platform — including its AI orchestration logic, prompts, workflows, user
            interface, branding, source code, and underlying models integration — remains the
            exclusive intellectual property of RADAR and its licensors.
          </p>
          <p>
            Users retain ownership of all business notes, primary-data inputs, and internal
            information that they upload into the Platform. We use this content solely to power
            your reports and improve your in-app experience.
          </p>
        </LegalSection>

        <LegalSection id="acceptable" title="5. Acceptable Use">
          <p>You agree NOT to use the Platform for:</p>
          <LegalList items={[
            "Illegal activities or to facilitate illegal activities by third parties.",
            "Unsolicited bulk outreach (spam), pretexting, or social engineering.",
            "Scraping, harvesting, or systematically extracting RADAR-generated outputs beyond reasonable internal use.",
            "Reverse engineering, decompiling, or attempting to extract the underlying models or prompts.",
            "Generating content that defames, harasses, discriminates, or targets individuals.",
            "Abusive load patterns intended to bypass quota limits or disrupt service.",
          ]} />
        </LegalSection>

        <LegalSection id="liability" title="6. Limitation of Liability">
          <p>
            RADAR does not guarantee the accuracy, completeness, timeliness, or business outcomes
            of any AI-generated intelligence. To the maximum extent permitted by law, RADAR shall
            not be liable for indirect, incidental, consequential, special, or punitive damages
            arising out of your use of the Platform.
          </p>
          <p>
            Total cumulative liability for any claim arising out of or relating to these Terms or
            the Platform shall not exceed the subscription fees paid by you in the twelve (12) months
            preceding the claim.
          </p>
        </LegalSection>

        <LegalSection id="changes" title="7. Changes to These Terms">
          <p>
            We may revise these Terms from time to time. Material changes will be communicated via
            email and posted on this page with an updated "Last updated" date. Continued use of the
            Platform after the effective date constitutes acceptance of the revised Terms.
          </p>
        </LegalSection>

        <LegalSection id="termination" title="8. Termination">
          <p>
            We may suspend or terminate accounts that materially breach these Terms (including
            unauthorised sharing, abuse, or non-payment). Users may delete their account at any time
            via the Contact page; data deletion is permanent after the read-only retention window.
          </p>
        </LegalSection>

        <LegalSection id="jurisdiction" title="9. Governing Law &amp; Jurisdiction">
          <p>
            These Terms are governed by the laws of India. Any disputes arising under or in
            connection with these Terms shall be subject to the exclusive jurisdiction of the courts
            located in <strong className="text-white">Chennai, Tamil Nadu, India</strong>.
          </p>
        </LegalSection>

        <LegalSection id="contact" title="10. Contact">
          <p>
            Questions about these Terms? Email <a className="text-[#0A84FF] hover:underline" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a>.
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
}
